import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Accessibility, ArrowLeft, Home as HomeIcon, Type, Contrast, Volume2, Ear, CheckCircle2, Loader2, AlertCircle, Send } from "lucide-react";
import { Seo, serviceJsonLd } from "../components/Seo";

const INCLUDED = [
  { Icon: Type, t: "Större text och tydligare färger", d: "Så att allt blir lättare att läsa på skärmen." },
  { Icon: Volume2, t: "Uppläsning", d: "Låt telefonen läsa upp text och meddelanden högt för dig." },
  { Icon: Ear, t: "Koppla din hörapparat till mobilen", d: "Så att du hör samtal klart och tydligt." },
  { Icon: Contrast, t: "Enklare startskärm", d: "Stora, tydliga knappar till det du använder mest." },
];

export default function Accessibility_() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", device: "", message: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.phone.trim()) { setError("Fyll i namn och telefonnummer."); return; }
    if (!validEmail) { setError("Skriv in en giltig e-postadress."); return; }
    setSubmitting(true);
    try {
      await api.post("/accessibility", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Något gick fel. Försök igen om en stund.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14">
      <Seo
        title="Anpassad tillgänglighet – enklare att se och höra"
        description="Vi anpassar telefon, surfplatta och dator för äldre och synskadade: större text, högre kontrast, uppläsning och att koppla hörapparat till mobilen. I lugn takt, hemma hos dig."
        path="/tillganglighet"
        jsonLd={serviceJsonLd({ name: "Anpassad tillgänglighet", description: "Anpassning av digitala enheter för äldre och synskadade.", path: "/tillganglighet" })}
      />
      <Link to="/" data-testid="access-back" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline">
        <ArrowLeft size={22} strokeWidth={2.5} /> Tillbaka till start
      </Link>

      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1A1A1A] mt-6 flex items-start gap-3">
        <Accessibility size={46} strokeWidth={2.5} className="text-[#0056D2] shrink-0 mt-1" /> Anpassad tillgänglighet – enklare att se och höra
      </h1>
      <h2 className="font-heading text-lg md:text-xl font-semibold text-[#4A4A4A] mt-4 leading-relaxed">
        Vi ställer in din telefon, surfplatta eller dator så att den blir lätt att använda – för dig som vill hålla kontakten med dina nära.
      </h2>

      {/* Value proposition */}
      <div className="bg-blue-50 border-2 border-blue-100 rounded-xl p-6 md:p-8 mt-8">
        <p className="text-lg md:text-xl leading-relaxed text-[#1A1A1A]">
          Skärmarna har blivit små och texten svår att läsa – men tekniken ska anpassas efter dig, inte
          tvärtom. Vi gör din telefon, surfplatta eller dator enklare att se och höra, så att du kan
          ringa, skriva och titta på bilder utan krångel. I lugn takt, hemma hos dig.
        </p>
      </div>

      {/* What's included */}
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A] mt-10">Det här hjälper vi dig med</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
        {INCLUDED.map(({ Icon, t, d }) => (
          <div key={t} className="bg-white border-2 border-gray-300 rounded-xl p-6 flex flex-col items-start">
            <span className="bg-blue-50 text-[#0056D2] rounded-xl p-3"><Icon size={30} strokeWidth={2} /></span>
            <h3 className="font-heading text-xl font-bold text-[#1A1A1A] mt-4">{t}</h3>
            <p className="text-base text-[#4A4A4A] mt-2">{d}</p>
          </div>
        ))}
      </div>

      {/* Form */}
      {done ? (
        <div data-testid="access-success" className="bg-white border-2 border-gray-300 rounded-xl p-8 mt-10 text-center">
          <span className="inline-flex bg-green-50 text-[#0B7A3F] rounded-full p-4">
            <CheckCircle2 size={56} strokeWidth={2} />
          </span>
          <h2 className="font-heading text-3xl font-bold text-[#1A1A1A] mt-5">Tack för din förfrågan!</h2>
          <p className="text-lg text-[#4A4A4A] mt-3">
            Jag har tagit emot dina uppgifter och återkommer så snart som möjligt för att boka en tid som passar dig.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-lg font-semibold text-[#0056D2] hover:underline mt-6">
            <HomeIcon size={22} strokeWidth={2.5} /> Tillbaka till start
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="bg-white border-2 border-gray-300 rounded-xl p-7 md:p-9 mt-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1A1A1A]">Boka hjälp med anpassning</h2>
          <p className="text-base text-[#4A4A4A] mt-2">Fyll i dina uppgifter så återkommer jag och bokar en tid. Det tar bara en minut.</p>

          <div className="space-y-6 mt-6">
            <div>
              <label htmlFor="a-name" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Namn</label>
              <input id="a-name" data-testid="access-name" value={form.name} onChange={set("name")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="För- och efternamn" />
            </div>
            <div>
              <label htmlFor="a-email" className="text-xl font-bold mb-2 block text-[#1A1A1A]">E-post</label>
              <input id="a-email" type="email" data-testid="access-email" value={form.email} onChange={set("email")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="namn@exempel.se" />
            </div>
            <div>
              <label htmlFor="a-phone" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Telefonnummer</label>
              <input id="a-phone" type="tel" data-testid="access-phone" value={form.phone} onChange={set("phone")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="07X-XXX XX XX" />
            </div>
            <div>
              <label htmlFor="a-device" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Vilken enhet vill du ha hjälp med? <span className="text-base font-medium text-[#4A4A4A]">(om du vet)</span></label>
              <input id="a-device" data-testid="access-device" value={form.device} onChange={set("device")}
                className="min-h-[56px] text-lg px-4 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2]" placeholder="t.ex. iPhone, Android-telefon, surfplatta eller dator" />
            </div>
            <div>
              <label htmlFor="a-message" className="text-xl font-bold mb-2 block text-[#1A1A1A]">Meddelande</label>
              <textarea id="a-message" data-testid="access-message" value={form.message} onChange={set("message")} rows={5}
                className="text-lg px-4 py-3 border-2 border-gray-400 rounded-lg w-full focus:border-[#0056D2] resize-y"
                placeholder="Berätta gärna vad som känns svårt idag (valfritt)" />
            </div>

            {error && (
              <p data-testid="access-error" className="flex items-center gap-2 text-[#D32F2F] text-lg font-semibold">
                <AlertCircle size={22} /> {error}
              </p>
            )}

            <button data-testid="access-submit" type="submit" disabled={submitting}
              className="inline-flex items-center justify-center gap-3 min-h-[64px] w-full px-8 bg-[#0056D2] text-white text-xl font-bold rounded-lg hover:bg-[#003E99] transition-colors disabled:opacity-60">
              {submitting ? <Loader2 className="animate-spin" size={26} /> : <Send size={26} strokeWidth={2.5} />}
              {submitting ? "Skickar…" : "Skicka förfrågan"}
            </button>

            <p data-testid="gdpr-notice" className="text-sm text-[#4A4A4A] leading-relaxed text-center">
              Din integritet är viktig för oss. Vi delar aldrig dina uppgifter med tredje part och hanterar all data säkert i enlighet med GDPR.
            </p>
          </div>
        </form>
      )}
    </div>
  );
}
